import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password reset — you can now log in");
      setTimeout(() => navigate("/login"), 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="max-w-sm border border-border p-8 rounded-sm" data-testid="reset-no-token">
          <h2 className="font-display text-2xl">Invalid link</h2>
          <p className="text-sm text-[#475569] mt-2">This reset link is missing a token. Please request a new one.</p>
          <Link to="/forgot-password" className="mt-4 inline-block text-xs uppercase tracking-wider text-[#2563EB]">← Forgot password</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-6" data-testid="reset-page">
      <div className="w-full max-w-sm border border-border p-8 rounded-sm">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 bg-[#2563EB] text-white flex items-center justify-center rounded-sm">
            <ShieldCheck size={18} weight="bold" />
          </div>
          <span className="font-display font-semibold">HRMS Suite</span>
        </div>
        <h2 className="font-display text-2xl tracking-tight font-medium">Set new password</h2>
        <p className="text-sm text-[#475569] mt-1">Choose a strong password you&rsquo;ll remember.</p>
        {done ? (
          <div className="mt-6 text-sm text-[#12B886]">Password updated — redirecting to login&hellip;</div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#475569]">New password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1 rounded-sm" data-testid="reset-new-password" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#475569]">Confirm password</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} className="mt-1 rounded-sm" data-testid="reset-confirm-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-sm uppercase tracking-wider" data-testid="reset-submit">
              Reset password
            </Button>
          </form>
        )}
        <div className="mt-6 text-xs text-[#475569]"><Link to="/login" className="hover:text-[#2563EB]">← Back to login</Link></div>
      </div>
    </div>
  );
}
